'use client';

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export function ProcessingSpinner({
  isOpen,
  onClose,
  title = 'Processing',
  processingMessage = 'Please wait while your request is being processed...',
  successMessage = 'Your request has been successfully completed!',
  errorMessage = 'There was an error processing your request.',
  status = 'processing',
  error = null,
  onRetry = null,
  showCloseOnSuccess = true,
  successActions = null, // Custom actions to show on success (replaces default Close button)
  processingIcon,
  successIcon,
  errorIcon,
}) {
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Handle retry
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
  };

  // Handle close with confirmation for success
  const handleClose = () => {
    if (status === 'success') {
      setShowConfirmation(true);
    } else {
      onClose();
    }
  };

  // Handle confirmed close
  const handleConfirmedClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  // Handle cancel confirmation
  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  // Prevent closing during processing, allow closing on error or success
  const handleOpenChange = (open) => {
    if (!open) {
      if (status === 'processing') {
        setShowConfirmation(true);
      } else {
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md bg-zinc-900/95 backdrop-blur-xl border-white/10 text-white">
        {/* Accessibility: Hidden title and description for screen readers */}
        <VisuallyHidden.Root>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {status === 'processing' && processingMessage}
            {status === 'success' && successMessage}
            {status === 'error' && (error || errorMessage)}
          </DialogDescription>
        </VisuallyHidden.Root>

        <div className="flex flex-col items-center py-8 px-4">
          {/* Status Indicator */}
          <div className="mb-6">
            {status === 'processing' && (
              <div className="relative">
                {processingIcon ? (
                  processingIcon
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 rounded-full bg-violet-900/20 flex items-center justify-center relative border border-violet-500/20">
                      <div className="absolute inset-0 rounded-full border-4 border-t-violet-500 border-r-violet-500/30 border-b-violet-500/10 border-l-violet-500/60 animate-spin"></div>
                      <Loader2 className="h-10 w-10 text-violet-500 animate-pulse" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {status === 'success' && (
              <div className="flex flex-col items-center">
                {successIcon ? (
                  successIcon
                ) : (
                  <div className="w-24 h-24 rounded-full bg-emerald-900/20 flex items-center justify-center border border-emerald-500/20">
                    <CheckCircle className="h-14 w-14 text-emerald-500" />
                  </div>
                )}
              </div>
            )}

            {status === 'error' && (
              <div className="flex flex-col items-center">
                {errorIcon ? (
                  errorIcon
                ) : (
                  <div className="w-24 h-24 rounded-full bg-red-900/20 flex items-center justify-center border border-red-500/20">
                    <XCircle className="h-14 w-14 text-red-500" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Status Text */}
          <h2 className="text-xl font-bold mb-2 text-center text-white">
            {status === 'processing' && title}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Error'}
          </h2>

          <p className="text-zinc-400 text-center mb-6 max-w-xs">
            {status === 'processing' && processingMessage}
            {status === 'success' && successMessage}
            {status === 'error' && (error || errorMessage)}
          </p>

          {/* Action Buttons */}
          {status === 'error' && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRetry}
                className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white"
              >
                Try Again
              </Button>
            </div>
          )}

          {status === 'success' && (
            successActions ? (
              // Render custom success actions if provided
              <div className="flex gap-2 flex-wrap justify-center">
                {successActions}
              </div>
            ) : showCloseOnSuccess ? (
              <Button
                onClick={onClose}
                className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white"
              >
                Close
              </Button>
            ) : null
          )}
        </div>
      </DialogContent>

      {/* Only show confirmation dialog during processing */}
      {status === 'processing' && (
        <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
          <AlertDialogContent className="bg-zinc-900/95 backdrop-blur-xl border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">
                Are you sure you want to close?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Your operation is still processing. Are you sure you want to
                close this dialog?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={handleCancelConfirmation}
                className="border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmedClose}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, close
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Dialog>
  );
}
